import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import type {
  GlassType,
  TintPercentage,
  TintSelections,
} from "../../utils/windowTinting";

type Props = {
  glassTypes: GlassType[];
  tintPercentages: TintPercentage[];
  selections: TintSelections;
  onChange: (glassTypeId: number, tintPercentageId: number | null) => void;
  className?: string;
};

function resolveTransmission(tint: TintPercentage): number | null {
  if (typeof tint.percentage === "number" && Number.isFinite(tint.percentage)) {
    return Math.max(0, Math.min(100, tint.percentage));
  }

  const fromName = tint.name.match(/\d{1,3}/);
  if (!fromName) return null;

  const value = Number(fromName[0]);
  return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
}

/**
 * One row per piece of glass; the customer sets a tint percentage on the rows
 * they want tinted and leaves the rest untouched.
 */
const WindowTintSelector = ({
  glassTypes,
  tintPercentages,
  selections,
  onChange,
  className,
}: Props) => {
  const { t } = useTranslation();
  const chosenCount = Object.keys(selections).length;

  return (
    <section className={`ac-tint${className ? ` ${className}` : ""}`}>
      <header className="ac-tint__head">
        <div>
          <h3 className="ac-tint__title">{t("services.tint.title")}</h3>
          <p className="ac-tint__hint">{t("services.tint.hint")}</p>
        </div>
        <span className="ac-tint__count" aria-live="polite">
          {t("services.tint.selected", { count: chosenCount })}
        </span>
      </header>

      <figure className="ac-tint__guide">
        <figcaption>{t("services.tint.guideCaption")}</figcaption>
        <ul className="ac-tint__guide-list" aria-label={t("services.tint.guideAlt")}>
          {tintPercentages.map((tint) => {
            const transmission = resolveTransmission(tint);
            const opacity =
              transmission == null
                ? 0.45
                : Math.max(0.15, Math.min(0.85, (100 - transmission) / 100));

            return (
              <li key={tint.id} className="ac-tint__guide-item">
                <span
                  className="ac-tint__guide-image"
                  style={
                    {
                      "--ac-tint-overlay-opacity": opacity,
                    } as CSSProperties
                  }
                >
                  <img
                    src={`${process.env.PUBLIC_URL ?? ""}/assets/imgs/shop/window-tint-indicator.jpg`}
                    alt=""
                    loading="lazy"
                    aria-hidden
                  />
                </span>
                <span className="ac-tint__guide-label">{tint.name}</span>
              </li>
            );
          })}
        </ul>
      </figure>

      <ul className="ac-tint__list">
        {glassTypes.map((glass) => {
          const selected = selections[glass.id];

          return (
            <li key={glass.id} className="ac-tint__row">
              <span className="ac-tint__glass" id={`tint-glass-${glass.id}`}>
                {glass.name}
              </span>

              <select
                className={`ac-tint__select${selected ? " is-selected" : ""}`}
                aria-labelledby={`tint-glass-${glass.id}`}
                value={selected ?? ""}
                onChange={(event) =>
                  onChange(
                    glass.id,
                    event.target.value ? Number(event.target.value) : null
                  )
                }
              >
                <option value="">{t("services.tint.none")}</option>
                {tintPercentages.map((tint) => (
                  <option key={tint.id} value={tint.id}>
                    {tint.name}
                  </option>
                ))}
              </select>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default WindowTintSelector;
